// src/services/onboardingProfile.js
import {
  setInitialConfigProperty,
  setInitialConfigExtraProperty,
} from "../redux/reducer/initialConfigSlice";
import { baseUrl, headersDefault } from "../redux/actions";
import { consumeAppleProviderName } from "./auth";

import { readAuth, writeAuth } from "./authStorage";
export const extractYMD = (v) => {
  const m = String(v ?? "").match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return { y: m[1], m: m[2], d: m[3] };
};

export const formatDDMMYY = (v) => {
  const ymd = extractYMD(v);
  if (!ymd) return "";
  const yy = String(ymd.y).slice(-2);
  return `${ymd.d}-${ymd.m}-${yy}`;
};

const trim = (v) => String(v ?? "").trim();
// Like trim, but also strips the invisible U+3164 (Hangul Filler) placeholder the
// backend seeds social/Apple accounts with for name/surname (plus zero-width
// chars) - so a placeholder-only name is treated as empty and onboarding prefills
// the real name. Scoped to the prefill so existing accounts aren't re-routed into
// onboarding.
const cleanName = (v) =>
  String(v ?? "")
    .replace(/[\u3164\u200B-\u200D\uFEFF]/g, "")
    .trim();
const num = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const safeJsonParse = (value) => {
  if (value == null) return null;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
};

// Stored by getUserInfo() bootstrap refresh (and also refreshed after acceptance)
export const loadAgreements = () => {
  const parsed = safeJsonParse(readAuth("adoc_agreements"));
  return Array.isArray(parsed) ? parsed : null;
};

export const requiredAgreementsAccepted = (agreements) => {
  if (!Array.isArray(agreements)) return false;
  return !agreements.some(
    (a) =>
      a &&
      a.is_enabled !== false &&
      a.is_required === true &&
      a.is_accepted !== true,
  );
};

/**
 * Decide if onboarding is incomplete
 */
export const isOnboardingIncomplete = (profile) => {
  const mc = profile?.master_club_subscriber || {};
  const user = mc?.user || profile?.user || {};

  const extras = Array.isArray(mc?.extras) ? {} : mc?.extras || {};
  const name = trim(user?.name) || trim(profile?.user?.name);
  const surname = trim(user?.surname) || trim(profile?.user?.surname);

  // Three screens since the client's changes of 14/09/2026: personal data,
  // profile, preferences.
  // - Sex and date of birth are required; height and weights are optional.
  // - Goals and "Cibi non graditi" are no longer asked, so they can't be required.
  // - The Terms are accepted on the sign-in screen instead of a checkbox here, so
  //   the agreements no longer block completion either.
  const personalOk = !!mc?.sex && !!mc?.birth_date;
  // Only name + surname. Country, province and phone are OPTIONAL per App Store
  // Guideline 5.1.1(v): the backend `mandatory-fields` flag + PublicProfileStep
  // decide whether to *ask* for them, but they must never block completion here.
  const profileOk = !!name && !!surname;
  const preferencesOk = extras?.type_of_diet_done === true;

  return !(personalOk && profileOk && preferencesOk);
};

/**
 * Where an unfinished onboarding continues: the first screen still missing
 * something, so a member who stopped halfway doesn't start again from the top.
 * Step numbers are the screens' own (1 personal data, 2 profile, 5 preferences),
 * which keeps the funnel's step ids comparable with earlier app versions.
 */
export const onboardingResumePath = (profile) => {
  const mc = profile?.master_club_subscriber || {};
  const user = mc?.user || profile?.user || {};

  if (!mc?.sex || !mc?.birth_date) return "/impostazioni-iniziali/1";

  // A new account's name is the invisible placeholder until the profile screen.
  const name = cleanName(user?.name) || cleanName(profile?.user?.name);
  const surname = cleanName(user?.surname) || cleanName(profile?.user?.surname);
  if (!name || !surname) return "/impostazioni-iniziali/2";

  return "/impostazioni-iniziali/5";
};

export const isItalyCountry = (countryText) =>
  /italia/i.test(String(countryText || ""));

export const hydrateGoalsCatalogFromApi = (goalsApiArray) => {
  if (!Array.isArray(goalsApiArray)) return [];

  // Expecting: [{ id, label, ... }]
  return goalsApiArray.map((g) => ({
    id: g?.id,
    label: g?.label ?? g?.name ?? "",
    ...g,
  }));
};

export const hydrateInitialConfigFromProfile = (dispatch, profile) => {
  const mc = profile?.master_club_subscriber || {};
  const user = profile?.user || {};

  dispatch(
    setInitialConfigProperty({ key: "sex_id", value: mc?.sex_id ?? null }),
  );
  dispatch(
    setInitialConfigProperty({ key: "height", value: mc?.height ?? null }),
  );
  dispatch(
    setInitialConfigProperty({ key: "weight", value: mc?.weight ?? null }),
  );
  dispatch(
    setInitialConfigProperty({
      key: "birth_date",
      value: mc?.birth_date ?? null,
    }),
  );

  // Consume the name Apple provided for THIS sign-in (one-shot). Use it to prefill
  // Nome/Cognome when the account has no real name yet (`cleanName` treats the
  // backend's invisible "ㅤ" placeholder as empty), AND — whenever Apple supplied
  // a name — flag it so PublicProfileStep LOCKS the fields (Apple SiwA: don't ask
  // the user to re-type it). Covers first-time sign-ins too, where the backend has
  // already seeded the real name onto the account.
  const appleName = consumeAppleProviderName();
  const cleanUserName = cleanName(user?.name);
  const cleanUserSurname = cleanName(user?.surname);
  dispatch(
    setInitialConfigProperty({
      key: "name",
      value: cleanUserName || appleName?.name || "",
    }),
  );
  dispatch(
    setInitialConfigProperty({
      key: "surname",
      value: cleanUserSurname || appleName?.surname || "",
    }),
  );
  try {
    if (appleName && (appleName.name || appleName.surname)) {
      localStorage.setItem("onboarding_name_from_apple", "1");
    } else if (cleanUserName) {
      localStorage.removeItem("onboarding_name_from_apple");
    }
  } catch (_) {}
  dispatch(
    setInitialConfigProperty({ key: "email", value: user?.email ?? "" }),
  );

  dispatch(
    setInitialConfigProperty({ key: "telephone", value: mc?.telephone ?? "" }),
  );
  dispatch(
    setInitialConfigProperty({ key: "nickname", value: mc?.nickname ?? "" }),
  );

  dispatch(
    setInitialConfigProperty({
      key: "country_id",
      value: mc?.country_id ?? null,
    }),
  );
  dispatch(
    setInitialConfigProperty({
      key: "country_text",
      value: mc?.country_text ?? "",
    }),
  );
  dispatch(
    setInitialConfigProperty({
      key: "province_id",
      value: mc?.province_id ?? null,
    }),
  );
  dispatch(
    setInitialConfigProperty({
      key: "province_text",
      value: mc?.province_text ?? "",
    }),
  );

  // Step 3 goals live inside extras.goals
  dispatch(
    setInitialConfigProperty({
      key: "extras",
      value: {
        ...(mc?.extras ?? {}),
        goals: mc?.extras?.goals ?? [],
      },
    }),
  );

  // Step 4 diet flags (diet_id deprecated)
  dispatch(
    setInitialConfigProperty({
      key: "vegetarian",
      value: mc?.vegetarian ?? null,
    }),
  );
  dispatch(
    setInitialConfigProperty({
      key: "gluten_free",
      value: mc?.gluten_free ?? null,
    }),
  );
  dispatch(setInitialConfigProperty({ key: "diet_id", value: null }));

  // keep_weight is still used by UI
  dispatch(
    setInitialConfigProperty({
      key: "keep_weight",
      value: mc?.keep_weight ?? false,
    }),
  );
};
// ---- Nutritional goals (NEW API) ----
export const fetchNutritionalGoalProtocols = async () => {
  const res = await fetch(
    `${baseUrl}/api/v1/master-club-nutritional-goals/list`,
    { headers: headersDefault() },
  );
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : data?.data ?? [];
};

export const flattenMatchedGoals = (protocols) => {
  const map = new Map();
  (protocols || []).forEach((p) => {
    (p?.matched_goals || []).forEach((g) => {
      const id = Number(g?.id);
      const name = g?.name;
      if (!Number.isFinite(id) || !name) return;
      if (!map.has(String(id))) map.set(String(id), { id, name });
    });
  });
  return Array.from(map.values());
};

// keep this internal (your existing logic)

const getSelectedGoalIdsFromProfile = (profile) => {
  const mc = profile?.master_club_subscriber ?? {};
  const extras = mc?.extras ?? {};
  const old = Array.isArray(extras?.goals) ? extras.goals : [];
  const candidates = mc?.goal_ids ?? mc?.nutritional_goal_ids ?? old;
  return Array.isArray(candidates)
    ? candidates
        .map((g) => Number(g?.id ?? g))
        .filter((n) => Number.isFinite(n))
    : [];
};

const safeJson = (s, fallback) => {
  try {
    return JSON.parse(s ?? "") ?? fallback;
  } catch {
    return fallback;
  }
};

// optional but recommended: persist to adoc_p so cold refresh works too
const patchStoredProfileGoals = (goalIds) => {
  try {
    const stored = safeJson(readAuth("adoc_p"), null);
    if (!stored) return;

    const sub =
      stored?.master_club_subscriber ??
      stored?.subscriber ??
      stored?.profile?.master_club_subscriber;

    if (!sub) return;

    const extras = Array.isArray(sub.extras) ? {} : sub.extras || {};
    const existing = Array.isArray(extras.goals) ? extras.goals : [];
    if (existing.length) return; // don't overwrite

    const newSub = { ...sub, extras: { ...extras, goals: goalIds } };

    // write back in the same shape we read from
    const next = { ...stored, master_club_subscriber: newSub };
    writeAuth("adoc_p", JSON.stringify(next));
  } catch {}
};
